import {
  formatInstant,
  normalizeListKind,
  resolveDisplayZone,
  type ListKind,
} from "@checklist/shared";
import type {
  ApiChecklist,
  ApiChecklistItem,
  ApiList,
  ApiListItem,
  ApiListSubgroup,
  ApiTemplateSection,
} from "../api.js";
import type { PdfDocModel, PdfGroup, PdfItem, PdfSection } from "./model.js";

const DEFAULT_FOOTER_LEFT = "neattick.com";

function footerLeft(): string {
  const siteUrl = import.meta.env.VITE_SITE_URL;
  if (!siteUrl) return DEFAULT_FOOTER_LEFT;

  try {
    return new URL(siteUrl).host || DEFAULT_FOOTER_LEFT;
  } catch {
    return DEFAULT_FOOTER_LEFT;
  }
}

function listEyebrow(kind: ListKind): string {
  return kind === "todo" ? "TO-DO LIST" : "PACKING LIST";
}

function templateEyebrow(kind: ListKind): string {
  return kind === "todo" ? "TO-DO TEMPLATE" : "PACKING TEMPLATE";
}

function pdfItem(item: { text: string; note?: string }): PdfItem {
  return {
    text: item.text,
    ...(item.note === undefined ? {} : { note: item.note }),
    checked: false,
  };
}

function listGroups(list: {
  items: ApiListItem[];
  subgroups: ApiListSubgroup[];
}): PdfGroup[] {
  return [
    { name: null, items: list.items.map(pdfItem) },
    ...list.subgroups.map((subgroup) => ({
      name: subgroup.name,
      items: subgroup.items.map(pdfItem),
    })),
  ];
}

function listItemCount(list: {
  items: ApiListItem[];
  subgroups: ApiListSubgroup[];
}): number {
  return list.items.length + list.subgroups.reduce(
    (total, subgroup) => total + subgroup.items.length,
    0
  );
}

function checklistItem(
  item: ApiChecklistItem,
  opts: { includeAttribution: boolean; memberName: (sub: string) => string },
  zone: string
): PdfItem {
  let attribution: string | undefined;
  if (opts.includeAttribution && item.checked && item.checkedBy) {
    attribution = opts.memberName(item.checkedBy);
    if (item.checkedAt) {
      const date = formatInstant(item.checkedAt, zone, { locale: "en-US" });
      attribution = `${attribution} · ${date}`;
    }
  }

  return {
    text: item.text,
    checked: item.checked,
    ...(attribution === undefined ? {} : { attribution }),
  };
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

function itemCount(sections: PdfSection[]): number {
  return sections.reduce(
    (total, section) =>
      total +
      section.groups.reduce(
        (sectionTotal, group) => sectionTotal + group.items.length,
        0
      ),
    0
  );
}

export function checklistToPdfDoc(
  chk: ApiChecklist,
  opts: {
    includeAttribution: boolean;
    memberName: (sub: string) => string;
    url?: string;
  }
): PdfDocModel {
  const zone = resolveDisplayZone();
  const kind = normalizeListKind(chk.kind);
  const sections = chk.sections.map((section) => ({
    name: section.name,
    groups: [
      {
        name: null,
        items: section.items.map((item) => checklistItem(item, opts, zone)),
      },
      ...section.subgroups.map((subgroup) => ({
        name: subgroup.name,
        items: subgroup.items.map((item) => checklistItem(item, opts, zone)),
      })),
    ],
  }));
  const totalItems = itemCount(sections);
  const completed = sections.reduce(
    (total, section) =>
      total +
      section.groups.reduce(
        (groupTotal, group) =>
          groupTotal + group.items.filter((item) => item.checked).length,
        0
      ),
    0
  );

  return {
    title: chk.name,
    eyebrow: listEyebrow(kind),
    meta: [
      plural(totalItems, "item"),
      plural(sections.length, "section"),
      ...(completed > 0
        ? [`${completed} ${kind === "todo" ? "done" : "packed"}`]
        : []),
    ],
    sections,
    footerLeft: footerLeft(),
    generatedAt: new Date(),
    displayZone: zone,
    ...(opts.url === undefined ? {} : { qrUrl: opts.url }),
  };
}

export function listToPdfDoc(list: {
  name: string;
  kind: ListKind;
  description?: string;
  items: ApiListItem[];
  subgroups: ApiListSubgroup[];
  url?: string;
}): PdfDocModel {
  const zone = resolveDisplayZone();
  const kind = normalizeListKind(list.kind);
  const totalItems = listItemCount(list);
  const description = list.description?.trim();

  return {
    title: list.name,
    eyebrow: listEyebrow(kind),
    meta: [
      plural(totalItems, "item"),
      ...(description ? [description.slice(0, 90)] : []),
    ],
    sections: [{ name: list.name, groups: listGroups(list) }],
    footerLeft: footerLeft(),
    generatedAt: new Date(),
    displayZone: zone,
    ...(list.url === undefined ? {} : { qrUrl: list.url }),
  };
}

function templateSectionToPdfSection(
  section: ApiTemplateSection,
  lists: ApiList[]
): PdfSection {
  if (section.type === "inline") {
    return {
      name: section.name,
      groups: [
        {
          name: null,
          items: section.items.map((item) => ({
            text: item.text,
            checked: false,
          })),
        },
      ],
    };
  }

  const list = lists.find((candidate) => candidate.id === section.listId);
  if (!list) {
    return {
      name: section.name,
      groups: [
        {
          name: null,
          items: [{ text: "(list unavailable)", checked: false }],
        },
      ],
    };
  }

  return {
    name: section.name,
    groups: listGroups(list),
  };
}

export function templateToPdfDoc(
  tmpl: {
    name: string;
    kind: ListKind;
    sections: ApiTemplateSection[];
    url?: string;
  },
  lists: ApiList[]
): PdfDocModel {
  const zone = resolveDisplayZone();
  const kind = normalizeListKind(tmpl.kind);
  const sections = tmpl.sections.map((section) =>
    templateSectionToPdfSection(section, lists)
  );

  return {
    title: tmpl.name,
    eyebrow: templateEyebrow(kind),
    meta: [plural(itemCount(sections), "item"), plural(sections.length, "section")],
    sections,
    footerLeft: footerLeft(),
    generatedAt: new Date(),
    displayZone: zone,
    ...(tmpl.url === undefined ? {} : { qrUrl: tmpl.url }),
  };
}
